import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { RecordingLinkLine } from "@/components/session/recording/RecordingLinkLine";
import { RecordingTile } from "@/components/session/recording/RecordingTile";
import { Button } from "@/components/ui/button";
import { useTenantConfig } from "@/hooks/use-tenant-config";
import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/lib/api/client";
import {
  sessionRecordingsApi,
  sessionRecordingsQueryKey,
  type LearnerSessionRecordings,
} from "@/lib/api/session-recordings";

/**
 * The recorded session on the session portal, mounted once. A teaching seat gets
 * the one "Recording" tile (`recording/RecordingTile`); a learning seat gets
 * the session's recording plan and, once one is published, the recording.
 */
export function RecordedClassJourney({ sessionId, classOrdinal, part = "all" }: { sessionId: string; classOrdinal?: number | null; /** A learner page may draw the plan notice and the recordings in different boxes. */ part?: "all" | "notice" | "recordings" }) {
  const { me } = useAuth();
  const identityKey = `${me?.tenant_id ?? "unknown"}:${me?.user_id ?? "unknown"}`;
  const recordings = useQuery({
    queryKey: sessionRecordingsQueryKey(sessionId, identityKey),
    queryFn: () => sessionRecordingsApi.get(sessionId),
    retry: false,
  });
  if (recordings.error instanceof ApiError && recordings.error.status === 404) return null;

  // The notice waits quietly: the recordings part owns the loading and failure words.
  if (part === "notice" && !recordings.data) return null;

  if (recordings.isPending) {
    return (
      <p role="status" className="flex min-h-11 items-center gap-2 text-meta text-muted-foreground">
        <Loader2 aria-hidden className="size-4 animate-spin" strokeWidth={1.75} />
        Loading the recording
      </p>
    );
  }

  if (!recordings.data) {
    return (
      <div className="grid gap-3">
        <p role="status" className="text-ui text-muted-foreground">
          The recording record could not be loaded. No publication state was inferred.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => void recordings.refetch()}>
            <RefreshCw aria-hidden className="size-4" strokeWidth={1.75} /> Try again
          </Button>
        </div>
      </div>
    );
  }

  if (recordings.data.role === "teaching") {
    // The tenant's typed custody chooses the journey, never a slug.
    if (recordings.data.custody === "provider_link") {
      // A recording Zoom discovered is still the session's recording, whatever the
      // custody: the tile is drawn beside the link line, so a published one keeps
      // its Withdraw and a teacher is not asked to paste a link IMAM already holds.
      return (
        <>
          <RecordingLinkLine
            key={`${identityKey}:${sessionId}`}
            sessionId={sessionId}
            identityKey={identityKey}
            projection={recordings.data}
            classOrdinal={classOrdinal}
          />
          {recordings.data.artifacts.length > 0 && (
            <RecordingTile
              key={`${identityKey}:${sessionId}:artifacts`}
              sessionId={sessionId}
              identityKey={identityKey}
              projection={recordings.data}
              classOrdinal={classOrdinal}
            />
          )}
        </>
      );
    }
    return (
      <RecordingTile
        key={`${identityKey}:${sessionId}`}
        sessionId={sessionId}
        identityKey={identityKey}
        projection={recordings.data}
        classOrdinal={classOrdinal}
      />
    );
  }
  return (
    <>
      {part !== "recordings" && <LearnerRecordingNotice projection={recordings.data} />}
      {part !== "notice" && <LearnerRecordedClass key={`${identityKey}:${sessionId}`} sessionId={sessionId} projection={recordings.data} />}
    </>
  );
}

/** The session's recording plan as a learner sees it (ADR 0028: the exact-session surface states the plan). */
function LearnerRecordingNotice({ projection }: { projection: LearnerSessionRecordings }) {
  const { recordingPolicy } = useTenantConfig();
  if (!recordingPolicy) return null;
  const notice = projection.notice;
  return (
    <section aria-labelledby="class-recording-plan" className="grid gap-2 border-t border-rule pt-4">
      <h3 id="class-recording-plan" className="text-meta font-semibold text-muted-foreground">Recording plan</h3>
      <div className="grid justify-items-start gap-3">
        <p className="text-meta text-foreground-soft">{recordingPolicy.class_notice}</p>
        {notice && (
          <p className="text-ui font-medium text-foreground">
            {notice.status === "opted_out"
              ? "The instructor plans to keep this session unrecorded."
              : "The instructor has recorded the notice plan for this session."}
          </p>
        )}
      </div>
    </section>
  );
}

function LearnerRecordedClass({
  sessionId,
  projection,
}: {
  sessionId: string;
  projection: LearnerSessionRecordings;
}) {
  const { recordingPolicy } = useTenantConfig();
  const [opening, setOpening] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [openLink, setOpenLink] = useState<{
    placementId: string;
    revision: number;
    url: string;
    label: string;
  } | null>(null);
  const openLinkRef = useRef<HTMLAnchorElement>(null);
  const projectionRef = useRef(projection);
  projectionRef.current = projection;
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  useEffect(() => {
    if (!openLink) return;
    const current = projection.recordings.find((recording) =>
      recording.placement_id === openLink.placementId
      && recording.revision === openLink.revision
      && recording.destination_available,
    );
    if (!current) setOpenLink(null);
  }, [openLink, projection]);

  async function openRecording(placementId: string) {
    const requested = projection.recordings.find((recording) => recording.placement_id === placementId);
    if (!requested?.destination_available) return;
    setOpening(placementId);
    setOpenLink(null);
    setMessage(null);
    try {
      const result = await sessionRecordingsApi.open(sessionId, placementId);
      if (!active.current) return;
      const current = projectionRef.current.recordings.find((recording) =>
        recording.placement_id === placementId
        && recording.revision === requested.revision
        && recording.destination_available,
      );
      if (!current) {
        setMessage("The recording changed while access was being checked. Request a new link from the current record.");
        return;
      }
      setOpenLink({ placementId, revision: requested.revision, url: result.url, label: result.label });
      setMessage("Recording link ready. Open it to continue in the provider.");
      requestAnimationFrame(() => openLinkRef.current?.focus());
    } catch (error) {
      if (!active.current) return;
      setOpenLink(null);
      // A link closed by its gate says why in the server's words; an artifact keeps the standing sentence.
      setMessage(error instanceof ApiError && error.status === 409
        ? (requested.target_kind === "link"
          ? error.message
          : "This recording is currently unavailable. Contact your instructor or recording support if you need an accessible alternative.")
        : "Your access could not be confirmed. Refresh the session before trying again.");
    } finally {
      if (active.current) setOpening(null);
    }
  }

  if (projection.recordings.length === 0 && projection.withdrawn.length === 0) return null;

  return (
    <section aria-labelledby="recorded-class-heading" className="grid gap-2 border-t border-rule pt-4">
      <h3 id="recorded-class-heading" className="text-meta font-semibold text-muted-foreground">Recorded session</h3>
      <div className="grid gap-3">
        {projection.recordings.map((recording) => (
          <div key={recording.placement_id} className="flex flex-wrap items-center justify-between gap-3 border-t border-rule py-3">
            <div>
              <p className="font-medium text-foreground">{recording.display_label}</p>
              <p className="text-meta text-muted-foreground">
                {recording.target_kind === "link" ? recording.provider_word : recording.file_type} · Published for this session
              </p>
              {recording.target_kind === "link" && projection.audience_sentence && (
                <p className="text-meta text-muted-foreground">{projection.audience_sentence}</p>
              )}
            </div>
            <Button type="button" variant="outline" disabled={opening !== null || !recording.destination_available} onClick={() => void openRecording(recording.placement_id)}>
              {opening === recording.placement_id && <Loader2 aria-hidden className="size-4 animate-spin" strokeWidth={1.75} />}
              Open recording
            </Button>
            {openLink?.placementId === recording.placement_id && (
              <Button asChild variant="outline">
                <a ref={openLinkRef} href={openLink.url} target="_blank" rel="noopener noreferrer">
                  {openLink.label}<ExternalLink aria-hidden className="size-4" strokeWidth={1.75} />
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </Button>
            )}
            {!recording.destination_available && (
              <p className="w-full text-meta text-muted-foreground">
                The published record is preserved, but its provider destination is unavailable.
                {recordingPolicy && <> Contact <a className="underline underline-offset-4" href={`mailto:${recordingPolicy.support_email}`}>{recordingPolicy.support_email}</a> for recording support.</>}
              </p>
            )}
          </div>
        ))}
        {projection.withdrawn.map((recording) => (
          <p key={recording.placement_id} className="border-t border-rule py-3 text-ui text-muted-foreground">
            {recording.display_label} was withdrawn. No recording destination is available.
          </p>
        ))}
        {message && <p role="status" aria-live="polite" className="text-ui text-muted-foreground">{message}</p>}
        {message?.includes("recording support") && recordingPolicy && (
          <a className="inline-flex min-h-11 items-center text-ui font-medium text-primary underline underline-offset-4" href={`mailto:${recordingPolicy.support_email}`}>
            Email {recordingPolicy.support_email}
          </a>
        )}
      </div>
    </section>
  );
}
